import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { filterForInstance } from '@libs/utils';
import { AnimationOptions, LottieComponent } from 'ngx-lottie';
import { debounceTime, fromEvent } from 'rxjs';
import { BaaSignupFlowService } from './baa-sign-up-flow.service';

const KEYBOARD_RESIZE_DEBOUNCE_MS = 150;

@Component({
  selector: 'lib-baa-signup-flow',
  imports: [RouterModule, LottieComponent],
  templateUrl: './baa-sign-up-flow.component.html',
  styleUrl: './baa-sign-up-flow.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [BaaSignupFlowService],
})
export class BaaSignupFlowComponent implements OnInit {
  @ViewChild('flowContainer') private readonly flowContainerRef?: ElementRef<HTMLDivElement>;

  protected readonly service = inject(BaaSignupFlowService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  public readonly lottieOptions: AnimationOptions = {
    path: '/assets/animations/loading-spinner.json',
  };

  public ngOnInit(): void {
    this.service.initialize();
    this.resetScrollOnNavigation();
    this.scrollFocusedFieldIntoViewOnKeyboardOpen();
  }

  private resetScrollOnNavigation(): void {
    this.router.events.pipe(filterForInstance(NavigationEnd), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      // Deferred so this runs after the newly activated step's own view init (e.g. the OTP
      // input's autofocus), which can otherwise re-scroll the container after we reset it.
      setTimeout(() => {
        if (this.flowContainerRef?.nativeElement) {
          this.flowContainerRef.nativeElement.scrollTop = 0;
        }
      });
    });
  }

  private scrollFocusedFieldIntoViewOnKeyboardOpen(): void {
    if (typeof window === 'undefined' || !window.visualViewport) {
      return;
    }

    const visualViewportResize$ = fromEvent(window.visualViewport, 'resize');

    visualViewportResize$.pipe(debounceTime(KEYBOARD_RESIZE_DEBOUNCE_MS), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      const activeElement = document.activeElement;
      if (this.flowContainerRef?.nativeElement && this.flowContainerRef.nativeElement.contains(activeElement)) {
        (activeElement as HTMLElement).scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    });
  }
}
